// Reading material
// https://en.wikipedia.org/wiki/ISO_4217#Minor_units_of_currency
// https://en.wikipedia.org/wiki/List_of_circulating_currencies

import {
  currencies,
  currencyDisplay,
  currencyMinorUnits,
  currencyName,
  currencyNarrowSymbol,
  currencySubUnit,
} from './currency';
import { isNullish } from './isNullish';
import { getNumberFormattingOptions } from './number';

export type CurrencyInfo = {
  displayName: string;
  defaultFractionDigits: number;
  subUnit: number;
  symbol: string | undefined;
  code: string;
  unknown: boolean;
};

export const UnknownCurrencyInfo: CurrencyInfo = {
  displayName: 'Unknown currency',
  // Assume 0 fraction digits
  defaultFractionDigits: 0,
  // Assume no micro unit
  subUnit: 1,
  symbol: '¤',
  code: '',
  unknown: true,
};

export function getCurrencyInfo(
  currencyCode?: string,
  minorUnits?: number,
): CurrencyInfo {
  if (!currencyCode || !currencies.includes(currencyCode)) {
    return UnknownCurrencyInfo;
  }
  if (!currencyCode || !currencies.includes(currencyCode)) {
    return UnknownCurrencyInfo;
  }

  return {
    displayName: currencyName(currencyCode),
    defaultFractionDigits: minorUnits ?? currencyMinorUnits(currencyCode),
    subUnit: currencySubUnit(currencyCode, minorUnits),
    symbol: currencyNarrowSymbol(currencyCode),
    code: currencyCode,
    unknown: false,
  };
}

export function getCurrencyInputProps(currencyCode?: string) {
  const currencyInfo = getCurrencyInfo(currencyCode);
  const step =
    currencyInfo.defaultFractionDigits === 0
      ? '1'
      : `.${'0'.repeat(currencyInfo.defaultFractionDigits - 1)}1`;
  const placeholder =
    currencyInfo.defaultFractionDigits === 0
      ? '0'
      : `0.${'0'.repeat(currencyInfo.defaultFractionDigits)}`;

  return {
    step,
    placeholder,
  };
}

////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Converts amount in minor units to major units representation, but does not format it
 * @param amount in minor units
 * @param currencyCode
 * @param options
 */
export function minorToMajor(
  amount: number,
  currencyCode?: string,
  options?: { useLocale?: boolean | string },
): string {
  const currencyInfo = getCurrencyInfo(currencyCode);
  const { decimalSeparator } = getNumberFormattingOptions(options);

  const majorValue = amount / currencyInfo.subUnit;

  // Determine the number of decimal places to use
  const decimalPlaces =
    amount % currencyInfo.subUnit === 0
      ? 0
      : currencyInfo.defaultFractionDigits;

  const valueAsString = majorValue.toFixed(decimalPlaces);

  return valueAsString.replace('.', decimalSeparator);
}

export function majorToMinor(
  majorAmount: string | number,
  currencyCode: string,
  options?: { useLocale?: string },
): number {
  const currencyInfo = getCurrencyInfo(currencyCode);
  const { decimalSeparator } = getNumberFormattingOptions(options);
  const majorAmountAsString =
    typeof majorAmount === 'string' ? majorAmount : `${majorAmount}`;

  // Replace the locale-specific decimal separator with a dot for parsing
  const normalizedAmount = majorAmountAsString.replace(decimalSeparator, '.');

  // Convert the major amount to a float and then to the minor unit
  const minorValue = parseFloat(normalizedAmount) * currencyInfo.subUnit;

  // Return the rounded value to ensure we get an integer
  return Math.round(minorValue);
}

////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Converts amount from currency to different currency while keeping the major amount the same
 *
 * Input
 * 1 JPN, EUR
 *
 * Output
 * 100 (because 100 in minor units for EUR is 1 EUR in major)
 *
 * Input
 * 100 EUR, JPN
 *
 * Output
 * 1 (because 100 EUR in minor is 1 EUR in major)
 *
 * @param amount in minor units
 * @param fromCurrencyCode
 * @param toCurrencyCode
 * @return amount in minor units for the new currency
 */
export function switchCurrenciesKeepMajorAmount(
  amount: number | string,
  fromCurrencyCode: string,
  toCurrencyCode: string,
) {
  const fromCurrencyInfo = getCurrencyInfo(fromCurrencyCode);
  const toCurrencyInfo = getCurrencyInfo(toCurrencyCode);

  // If same sub units, no conversion needed
  if (fromCurrencyInfo.subUnit === toCurrencyInfo.subUnit) {
    return amount;
  }

  const amountInMajorForFromCurrencyCode = minorToMajor(
    Number(amount),
    fromCurrencyCode,
    { useLocale: false },
  );

  return parseMoney(amountInMajorForFromCurrencyCode, toCurrencyCode, {
    useLocale: false,
  });
}

////////////////////////////////////////////////////////////////////////////////////////////////////

export type FormatMoneyOptions = {
  /**
   * @default false
   */
  omitSymbol?: boolean;

  /**
   * @default false
   */
  shorten?: boolean;

  /**
   * Set to true when the input value to be formatted is in major units and doesn't need to be converted
   * diviting it by the sub units
   *
   * TODO: rename this as it doesn't make any sense
   *
   * @example
   *
   * formatMoney(15, 'EUR'); // returns EUR 0.15
   * formatMoney(15.5, 'EUR', { hundreds: true }); // returns EUR 15.50
   *
   * @default false
   */
  hundreds?: boolean;

  /**
   * Set this value if you want to override the primer's default minor units (number of fraction digits).
   */
  minorUnitsOverride?: number;

  /**
   * @default undefined
   *
   * undefined - uses user's locale
   * string - uses that given locale
   */
  locale?: string;

  /**
   * @default false
   *
   * When true, disables rounding of the number
   */
  noRounding?: boolean;

  numberFormatOptions?: Intl.NumberFormatOptions;
};

/**
 * Formats a monetary value according to currency and locale rules
 * @param inputValue The value to format (in minor units unless hundreds=true)
 * @param currencyCode The ISO 4217 currency code (e.g., 'USD', 'EUR', 'GBP')
 * @param options Formatting options
 * @param options.omitSymbol Omit the currency code from output (default: false)
 * @param options.shorten Use compact notation for large numbers (default: false)
 * @param options.hundreds Input is in major units and doesn't need conversion (default: false)
 * @param options.minorUnitsOverride Override the currency's default minor units
 * @param options.locale Specific locale to use (default: user's locale)
 * @param options.noRounding Disable rounding of numbers (default: false)
 * @param options.numberFormatOptions Additional Intl.NumberFormat options
 * @returns Formatted money string or null for invalid input
 *
 * @example
 * // Basic formatting (input in minor units)
 * formatMoney(1234, 'USD') // Returns 'USD 12.34'
 * formatMoney(1234, 'JPY') // Returns 'JPY 1,234'
 *
 * @example
 * // Input in major units
 * formatMoney(12.34, 'USD', { hundreds: true }) // Returns 'USD 12.34'
 *
 * @example
 * // Omit currency symbol
 * formatMoney(1234, 'USD', { omitSymbol: true }) // Returns '12.34'
 *
 * @example
 * // Compact notation for large numbers
 * formatMoney(1234567, 'USD', { shorten: true }) // Returns 'USD 12K'
 *
 * @example
 * // Custom locale
 * formatMoney(1234, 'EUR', { locale: 'de-DE' }) // Returns 'EUR 12,34'
 *
 * @example
 * // No rounding
 * formatMoney('12.3456', 'USD', { noRounding: true }) // Returns 'USD 12.3456'
 *
 * @example
 * // Invalid input
 * formatMoney(null, 'USD') // Returns null
 */
export function formatMoney(
  inputValue: number | string | null | undefined,
  currencyCode?: string,
  options: FormatMoneyOptions = {},
): string | null {
  if (isNullish(inputValue)) {
    return null;
  }

  const {
    locale,
    minorUnitsOverride,
    numberFormatOptions,
    omitSymbol,
    shorten,
    hundreds = false,
    noRounding = false,
  } = options;

  const { subUnit, code, defaultFractionDigits } = getCurrencyInfo(
    currencyCode,
    minorUnitsOverride,
  );

  if (noRounding) {
    const { decimalSeparator } = getNumberFormattingOptions({
      useLocale: locale,
    });

    let rawInteger: string;
    let rawDecimal: string;

    if (typeof inputValue === 'number') {
      // Use toFixed with sane precision to avoid float artifacts
      const safeDecimalStr = inputValue.toFixed(8).replace(/\.?0+$/, '');
      [rawInteger, rawDecimal = ''] = safeDecimalStr.split('.');
    } else if (typeof inputValue === 'string') {
      // Validate the string matches the locale's decimal separator
      const separatorCount = (
        inputValue.match(new RegExp(`\\${decimalSeparator}`, 'g')) || []
      ).length;
      if (separatorCount > 1) {
        return '0';
      }

      if (decimalSeparator === ',' && inputValue.includes('.')) {
        return '0';
      }
      if (decimalSeparator === '.' && inputValue.includes(',')) {
        return '0';
      }

      [rawInteger, rawDecimal = ''] = inputValue.split(decimalSeparator);
    } else {
      return '0';
    }

    const parsedInteger = Number(rawInteger);
    if (isNaN(parsedInteger)) {
      return '0';
    }

    const formattedInteger = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(parsedInteger);

    let trimmedDecimal = rawDecimal.replace(/0+$/, '');
    if (trimmedDecimal.length < 2) {
      trimmedDecimal = trimmedDecimal.padEnd(2, '0');
    }

    const formattedValue = `${formattedInteger}${decimalSeparator}${trimmedDecimal}`;
    return !omitSymbol && code ? `${code} ${formattedValue}` : formattedValue;
  }

  const inputValueAsNumber = Number(inputValue);
  if (isNaN(inputValueAsNumber)) {
    return '0';
  }

  const value = hundreds ? inputValueAsNumber : inputValueAsNumber / subUnit;

  return new Intl.NumberFormat(locale, {
    ...(omitSymbol || !code
      ? {
          minimumFractionDigits: defaultFractionDigits,
          maximumFractionDigits: defaultFractionDigits,
        }
      : {
          currency: code,
          style: 'currency',
          currencyDisplay: 'code',
        }),
    ...(shorten ? { notation: 'compact' } : undefined),
    ...numberFormatOptions,
  }).format(value);
}

type ParseMoneyOptions = {
  /**
   * @default true
   *
   * true - uses user's locale
   * false - uses en-US
   * string - uses that given locale
   */
  useLocale?: boolean | string;
};

/**
 * @returns monetary value in correct micro units for given currency
 */
export function parseMoney(
  value: string,
  currencyCode?: string,
  options: ParseMoneyOptions = {},
) {
  if (value === null || typeof value === 'undefined') {
    return null;
  }

  const currencyInfo = getCurrencyInfo(currencyCode);

  const { useLocale = true } = options;

  const { thousandsSeparator, decimalSeparator } = getNumberFormattingOptions({
    useLocale,
  });

  const valueCleaned = value
    .trim()
    .replace(/ /g, '') // If spaces are present, they always serve as thousands separators or extra characters
    .replaceAll(thousandsSeparator, '') // Removes thousands separators
    .replaceAll(decimalSeparator, '.') // Replace decimal separator with `.`
    .replace(/[^0-9.e]/g, ''); // Removes any non digit or dot characters

  // 1. Parse value 1st time to get a number, then round to the currency default fraction digits
  const valueParsed = parseFloat(valueCleaned).toFixed(
    currencyInfo.defaultFractionDigits,
  );

  // 2. Parse adjusted value again
  const valueAdjusted = parseFloat(valueParsed);

  // 3. multiply to get micro unit representation
  const valueMultiplied = valueAdjusted * currencyInfo.subUnit;

  // 4. guard against accidental floats at this point that can result from step 3
  return Math.round(valueMultiplied);
}

export function isValidMoneyInput(
  value: string,
  options: ParseMoneyOptions = {},
): boolean {
  const { useLocale = true } = options;

  const { thousandsSeparator, decimalSeparator } = getNumberFormattingOptions({
    useLocale,
  });

  // Intentionally including dot, comma and space as allowed at this stage
  // eslint-disable-next-line no-useless-escape
  const unallowedCharactersPattern = `[^0-9\\${thousandsSeparator}\\${decimalSeparator}\ \.\,]`;
  const unallowedCharacters = new RegExp(unallowedCharactersPattern, 'g');

  const valueTrimmed = value.replace(unallowedCharacters, '').trim();

  // Input must start and end with digit
  if (
    !/^[0-9]+$/.test(valueTrimmed[0]) ||
    !/^[0-9]+$/.test(valueTrimmed[valueTrimmed.length - 1])
  ) {
    return false;
  }

  const [integer, decimal, ...others] = valueTrimmed.split(decimalSeparator);

  // If multiple decimal separators were present, input is incorrect
  if (others && others.length > 0) {
    return false;
  }

  // Either no decimal part or just digits present
  const isValidDecimal = !decimal || /^[0-9]+$/.test(decimal);

  // If spaces are present, they always serve as thousands separators or extra characters
  const adjustedInteger = integer.replace(/ /g, '');

  const integerParts = adjustedInteger.split(thousandsSeparator);

  // Either one big digits-only part or each part must be digits-only
  const isValidInteger =
    integerParts.length === 1
      ? /^[0-9]+$/.test(integerParts[0])
      : integerParts.every((int, i) => {
          if (i === 0) {
            return int.length < 4 && int.length > 0 && /^[0-9]+$/.test(int);
          }
          return int.length === 3 && /^[0-9]+$/.test(int);
        });

  return isValidDecimal && isValidInteger;
}

// Useful for <SelectWithSearch />
export const currenciesAsSelectOptions = currencies.map((k) => ({
  value: k,
  label: k,
  secondaryLabel: currencyDisplay(k) || UnknownCurrencyInfo.displayName, // TODO: check if that's OK to standardize
}));
